import { useNavigate } from "@solidjs/router";
import { Send, Trash2, X } from "lucide-solid";
import { type Component, createEffect, createSignal, For, onMount, Show } from "solid-js";
import { AgentClient, runToolLoop, type ChatMessage as Msg, type ToolCall, type Tool } from "../../lib/agent-sdk";
import { buildBookingTools } from "../../lib/agent-tools";
import { getClaims } from "../../lib/auth";
import { useAgentUi, useAskUserTool } from "../../lib/solid-agent-ui";
import { Button } from "../ui/button";
import { ApprovalDialog } from "./ApprovalDialog";
import {
  appendMessage,
  chatMessages,
  chatSending,
  clearChat,
  setChatOpen,
  setChatSending,
  updateLastAssistantContent,
} from "./chat-store";
import { ChatMessage } from "./ChatMessage";

interface PendingApproval {
  toolName: string;
  args: unknown;
  risk: "low" | "medium" | "high";
  resolve: (approved: boolean) => void;
}

const GATEWAY_BASE = (import.meta.env.VITE_AI_GATEWAY_BASE as string | undefined) ?? "";
const GATEWAY_KEY = (import.meta.env.VITE_AI_GATEWAY_KEY as string | undefined) ?? "";

export const ChatPanel: Component = () => {
  const [input, setInput] = createSignal("");
  const [error, setError] = createSignal<string | null>(null);
  const [pendingApproval, setPendingApproval] = createSignal<PendingApproval | null>(null);
  let scrollRef: HTMLDivElement | undefined;
  const navigate = useNavigate();

  // Mutation tools route → form prefill → user submit = approve. Plugin
  // solid-agent-ui quản lý pending state + navigate + clear params.
  // Navigation tools (view_*) sinh từ BOOKING_ROUTES registry — typed schema
  // chống AI hallucinate path. Pass navigate trực tiếp vào buildBookingTools.
  const agentUi = useAgentUi();
  const { executeViaForm } = agentUi;
  const askUserTool = useAskUserTool();
  // Compose: app-specific tools + plugin's ask_user clarification tool.
  const tools = [...buildBookingTools(navigate, executeViaForm), askUserTool as unknown as Tool];

  const client = new AgentClient({
    baseUrl: GATEWAY_BASE,
    apiKey: GATEWAY_KEY,
    userId: () => getClaims()?.sub ?? null,
  });

  const scrollToBottom = () => {
    if (scrollRef) {
      queueMicrotask(() => {
        scrollRef!.scrollTop = scrollRef!.scrollHeight;
      });
    }
  };

  onMount(scrollToBottom);

  // Khi AI emit ask_user → plugin set pendingQuestion → render bubble + chuyển
  // submit handler sang "trả lời" mode.
  createEffect(() => {
    const pq = agentUi.pendingQuestion();
    if (pq) {
      appendMessage({
        role: "assistant",
        content: pq.question,
        ask_question: true,
        tool_trace: [],
      });
      scrollToBottom();
    }
  });

  const sendPrompt = async (text: string) => {
    setError(null);
    setChatSending(true);

    appendMessage({ role: "user", content: text });
    scrollToBottom();

    // Build history payload từ store (chỉ giữ role + content + tool fields).
    const history: Msg[] = chatMessages().map((m) => {
      const base: Msg = { role: m.role, content: m.content };
      if (m.tool_calls) base.tool_calls = m.tool_calls;
      if (m.tool_call_id) base.tool_call_id = m.tool_call_id;
      if (m.name) base.name = m.name;
      return base;
    });

    // KHÔNG accumulate AI delta vào bubble nữa — model glm dễ degenerate
    // (spam role tokens). Chỉ track trace (tool execution log). Summary phase
    // sẽ append message rõ ràng cuối flow.
    const trace: Array<{ name: string; ok: boolean; error?: string }> = [];
    let traceBubbleInit = false;
    const ensureTraceBubble = () => {
      if (!traceBubbleInit) {
        appendMessage({ role: "assistant", content: "", tool_trace: [] });
        traceBubbleInit = true;
      }
    };

    try {
      await runToolLoop({
        client,
        tools,
        messages: history,
        config: {
          system: [
            "Bạn là trợ lý booking. Trả lời ngắn gọn tiếng Việt. Dùng tool khi cần.",
            "",
            "═══ QUY TẮC TOOL CALLING ═══",
            "",
            "1. CREATE vs BOOK vs FIND — PHẢI phân biệt:",
            "   • Tạo MỚI (vd 'tạo group', 'tạo item Z1', 'tạo 3 ticket') → create_group / create_booking_item.",
            "     KHÔNG cần find/lookup vì item chưa tồn tại.",
            "   • Reserve/book item ĐÃ tồn tại ('đặt Z1', 'book ticket Z1') → find_item_by_name → book_item.",
            "   • Sửa/xoá item ĐÃ tồn tại → find_*_by_name → update_* / delete_*.",
            "",
            "2. Khi nhắc TÊN entity ĐÃ tồn tại: find_*_by_name TRƯỚC để lấy real ID.",
            "   KHÔNG đoán UUID. KHÔNG dùng tên như ID.",
            "",
            "3. Navigation: dùng `view_*` tools với REAL UUID. KHÔNG có navigate_to free-form.",
            "",
            "4. Chained tools (B cần output A): emit TỪNG tool, đợi result ở turn sau rồi emit tiếp.",
            "   KHÔNG batch khi có dependencies.",
            "",
            "5. KHÔNG dùng placeholder ('ID của X', '<id>', 'TODO') — luôn real value.",
            "",
            "6. HOÀN THÀNH HẾT bước user yêu cầu, KHÔNG dừng giữa chừng.",
            "   Sau mỗi tool result: user yêu cầu N bước, đã làm bao nhiêu? Còn thiếu thì emit tiếp.",
            "",
            "7. KHI THIẾU INFO → gọi ask_user, KHÔNG đoán/bịa value.",
            "   • User prompt thiếu required arg (vd 'tạo group' không nói tên) → ask_user({question:'Group tên là gì?'}).",
            "   • Ambiguous (vd 'xoá Z1' nhưng có 2 item tên Z1) → ask_user({question:'Có 2 item tên Z1, ID nào: a/b?'}).",
            "   • KHÔNG dùng default value (vd name='default', timeout=60) cho field user không cung cấp.",
            "   • Sau khi ask_user resolve, dùng user_answer làm value cho tool call tiếp theo.",
            "",
            "═══ VÍ DỤ ═══",
            "",
            "User: 'Tạo group SFO với 3 item Z1/Z2/Z3 mỗi cái 10 slot'",
            "  Iter 1: create_group({name:'SFO'}) → result {id:'grp-x'}",
            "  Iter 2: create_booking_item({name:'Z1',total_slots:10,group_id:'grp-x'}) → {id:'i1'}",
            "  Iter 3: create_booking_item({name:'Z2',total_slots:10,group_id:'grp-x'}) → {id:'i2'}",
            "  Iter 4: create_booking_item({name:'Z3',total_slots:10,group_id:'grp-x'}) → {id:'i3'}",
            "  Iter 5: Done, báo 'Đã tạo group + 3 item'.",
            "  ❌ KHÔNG emit find_item_by_name (Z1 chưa tồn tại lúc tạo).",
            "  ❌ KHÔNG emit book_item (user nói TẠO, không phải BOOK).",
            "",
            "User: 'Book ticket Z1'",
            "  Iter 1: find_item_by_name({name:'Z1'}) → [{id:'abc',...}]",
            "  Iter 2: book_item({item_id:'abc'}) → user click Book → result.",
            "",
            "User: 'Tạo booking group' (thiếu tên)",
            "  Iter 1: ask_user({question:'Group tên là gì?'}) → user_answer:'SFO'",
            "  Iter 2: create_group({name:'SFO'}) → success.",
          ].join("\n"),
        },
        maxIterations: 15,
        // onDelta intentionally omitted — model output (raw tool JSON / role
        // token degeneration) không hữu ích cho user. Chỉ hiện tool trace +
        // summary cuối.
        onToolCallEnd: (call, result) => {
          trace.push({
            name: call.function.name,
            ok: result.ok,
            ...(result.ok ? {} : { error: result.error }),
          });
          ensureTraceBubble();
          updateLastAssistantContent("", trace);
          scrollToBottom();
        },
        onApprovalRequired: (call: ToolCall, tool: Tool) =>
          new Promise<boolean>((resolve) => {
            let parsedArgs: unknown;
            try {
              parsedArgs = call.function.arguments ? JSON.parse(call.function.arguments) : {};
            } catch {
              parsedArgs = call.function.arguments;
            }
            setPendingApproval({
              toolName: tool.name,
              args: parsedArgs,
              risk: tool.risk ?? "medium",
              resolve,
            });
          }),
        // Sau khi tools chạy xong, yêu cầu AI tổng kết → append summary bubble riêng.
        requestSummary: true,
        onSummary: (text) => {
          appendMessage({
            role: "assistant",
            content: text,
            summary: true,
            tool_trace: [],
          });
          scrollToBottom();
        },
        onError: (err) => {
          setError(`${err.code}: ${err.message}`);
        },
      });
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setChatSending(false);
    }
  };

  const submit = (e?: Event) => {
    e?.preventDefault();
    const text = input().trim();
    if (!text) return;

    // Nếu AI đang chờ trả lời clarification → submit answer thay vì start turn mới.
    const pq = agentUi.pendingQuestion();
    if (pq) {
      setInput("");
      appendMessage({ role: "user", content: text });
      scrollToBottom();
      agentUi.submitAnswer(text);
      return;
    }

    if (chatSending()) return;
    setInput("");
    void sendPrompt(text);
  };

  const onKey = (e: KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  return (
    <div class="fixed bottom-4 left-4 z-50 w-[360px] max-w-[calc(100vw-2rem)] h-[560px] max-h-[calc(100vh-2rem)] bg-card border-2 border-blue-500/30 rounded-lg shadow-xl flex flex-col">
      <div class="flex items-center justify-between px-3 py-2 border-b bg-blue-500/5">
        <div class="font-medium text-sm flex items-center gap-2">
          <span class="inline-block h-2 w-2 rounded-full bg-blue-500" />
          Broker (Approach B)
        </div>
        <div class="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            class="h-7 w-7"
            title="Clear"
            onClick={() => {
              agentUi.cancelQuestion("chat cleared");
              clearChat();
            }}
          >
            <Trash2 class="h-3.5 w-3.5" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            class="h-7 w-7"
            title="Close"
            onClick={() => {
              agentUi.cancelQuestion("chat closed");
              setChatOpen(false);
            }}
          >
            <X class="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div ref={scrollRef} class="flex-1 overflow-y-auto p-3 space-y-3">
        <Show
          when={chatMessages().length > 0}
          fallback={
            <div class="text-sm text-muted-foreground text-center py-8">
              <p class="mb-2">Approach B: client-side tool calling.</p>
              <ul class="text-xs space-y-1 text-left inline-block">
                <li>• "Liệt kê groups"</li>
                <li>• "Mở dialog tạo group tên Test"</li>
                <li>• "Đi tới trang admin/items"</li>
              </ul>
            </div>
          }
        >
          <For each={chatMessages()}>{(m) => <ChatMessage message={m} />}</For>
        </Show>
        <Show when={chatSending()}>
          <div class="text-xs text-muted-foreground italic">đang stream…</div>
        </Show>
      </div>

      <Show when={error()}>
        <div class="px-3 py-2 text-xs bg-destructive/10 text-destructive border-t">{error()}</div>
      </Show>

      <Show when={agentUi.pendingQuestion()}>
        <div class="px-3 py-1.5 text-xs bg-amber-500/10 text-amber-700 dark:text-amber-300 border-t border-amber-500/30 flex items-center gap-1.5">
          <span>🤔 AI đang chờ bạn trả lời câu hỏi</span>
        </div>
      </Show>

      <form onSubmit={submit} class="border-t p-2 flex items-end gap-2">
        <textarea
          class="flex-1 resize-none rounded-md border bg-background px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
          rows="2"
          placeholder={agentUi.pendingQuestion() ? "Trả lời AI…" : "Hỏi gì đó (broker)…"}
          value={input()}
          onInput={(e) => setInput(e.currentTarget.value)}
          onKeyDown={onKey}
          disabled={chatSending() && !agentUi.pendingQuestion()}
        />
        <Button
          type="submit"
          size="icon"
          class="h-9 w-9 bg-blue-600 hover:bg-blue-700"
          disabled={(chatSending() && !agentUi.pendingQuestion()) || !input().trim()}
        >
          <Send class="h-4 w-4" />
        </Button>
      </form>

      <Show when={pendingApproval()}>
        {(p) => (
          <ApprovalDialog
            open={true}
            toolName={p().toolName}
            args={p().args}
            risk={p().risk}
            onApprove={() => {
              p().resolve(true);
              setPendingApproval(null);
            }}
            onReject={() => {
              p().resolve(false);
              setPendingApproval(null);
            }}
          />
        )}
      </Show>
    </div>
  );
};
